import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Star } from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { api, fmtDate, formatApiError, waLink } from "@/lib/api";
import { feedbackText } from "@/lib/messages";
import { useAdminSettings } from "@/lib/useAdminSettings";
import { Empty, PageHeader, Panel } from "@/components/admin/common";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";

const WINDOWS = ["7", "30", "90"];

const Status = ({ b }) => {
  if (b.feedback_sent_at) return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700" data-testid={`feedback-status-${b.id}`}><CheckCircle2 className="h-3.5 w-3.5" />Sent {fmtDate(b.feedback_sent_at)}{b.feedback_sent_via === "auto" ? " · auto" : ""}</span>;
  const [text, cls] = b.days_since_end === 0 ? ["Trip ends today · send tomorrow", "bg-sky-50 text-sky-800"]
    : b.days_since_end === 1 ? ["Send today", "bg-coral text-white"] : [`Trip ended ${b.days_since_end} days ago`, "bg-amber-50 text-amber-800"];
  return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`} data-testid={`feedback-status-${b.id}`}>{text}</span>;
};

export default function Feedback() {
  const settings = useAdminSettings();
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/feedback", { params: { days } }).then((r) => setData(r.data)), [days]);
  useEffect(() => { load(); }, [load]);

  const sent = async (b) => {
    try {
      const { data: nb } = await api.post(`/admin/bookings/${b.id}/feedback-sent`);
      setData((d) => ({ ...d, bookings: d.bookings.map((x) => (x.id === nb.id ? { ...x, ...nb } : x)) }));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const pending = data?.bookings.filter((b) => !b.feedback_sent_at && b.days_since_end >= 1).length || 0;

  return (
    <div data-testid="admin-feedback-page">
      <PageHeader title="Feedback requests" subtitle="Thank guests 1 day after their trip and ask for a Google review and their trip photos." />
      <div className="grid gap-4 sm:grid-cols-3 mb-4">
        <Panel className="p-5"><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Trips ended</p><p className="mt-2 font-mono text-2xl font-semibold text-navy" data-testid="feedback-count">{data?.count ?? "-"}</p></Panel>
        <Panel className="p-5"><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Waiting for a thank-you</p><p className="mt-2 font-mono text-2xl font-semibold text-coral" data-testid="feedback-pending">{data ? pending : "-"}</p></Panel>
        <Panel className="p-5">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Trips ended in last</p>
          <div className="mt-2 flex gap-1.5">{WINDOWS.map((w) => (
            <button key={w} onClick={() => setDays(w)} data-testid={`feedback-window-${w}`}
              className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${days === w ? "bg-ocean text-white border-ocean" : "border-slate-200 text-slate-600 hover:border-ocean"}`}>{w} days</button>
          ))}</div>
        </Panel>
      </div>
      {settings && !settings.review_link && <p className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="feedback-no-link">Add your Google review link in Company Settings to include it in the message.</p>}
      {!data ? <Skeleton className="h-40 rounded-2xl" /> : data.bookings.length === 0 ? (
        <Panel><Empty testId="feedback-empty"><Star className="h-8 w-8 mx-auto mb-3 text-amber-400" />No trips ended in the last {days} days.</Empty></Panel>
      ) : (
        <div className="space-y-3">
          {data.bookings.map((b) => (
            <Panel key={b.id} className="p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`feedback-row-${b.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap"><p className="font-semibold text-navy">{b.customer_name}</p><Status b={b} /></div>
                <p className="text-xs text-slate-500 truncate">{b.booking_no} · {b.phone} · {b.package_name || "Custom package"}</p>
                <p className="text-xs text-slate-500 mt-1">Trip {fmtDate(b.travel_date)} – {fmtDate(b.trip_end)}</p>
              </div>
              <a href={waLink(b.phone, feedbackText(b, settings))} target="_blank" rel="noreferrer" onClick={() => sent(b)} data-testid={`feedback-send-${b.id}`}
                className="inline-flex items-center justify-center gap-2 h-10 px-5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors">
                <WhatsAppIcon className="h-4 w-4" />{b.feedback_sent_at ? "Send again" : "Send thank-you"}
              </a>
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
